import { isEmpty } from 'lodash';

import { REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import { NOTIFICATION_TYPES } from 'constants/notificationTypes';
import { PROJECT_STATUSES } from 'constants/commonProjects';
import { ACTION_FIELD_LABELS } from 'constants/actionTracker';
import { ACTIVITY_LOG_CATEGORIES, TAG_TYPES_BY_LOG_CATEGORY } from 'constants/activityLog';
import { formatDate, getLastKeyEventDatePlaceholder, getTomorrowDate } from 'helper/DateLib';
import { formatProjectData } from 'helper/formatProjectData';
import { downloadFile } from 'helper/downloadFile';
import { formatSearchParams } from 'helper/formatSearchParams';
import { addNotification } from 'helper/notifications';
import { baseApi } from 'store/baseApi';

const PROJECT_MUTATION_NON_DRAFT_TAGS = [
  { type: API_TAG_TYPES.MASTER_DASHBOARD_CULTURAL_PROJECTS, id: 'LIST' },
  { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
  { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_GRAPH },
  { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_CULTURE_PROJECTS, id: 'LIST' },
  { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
  { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
  { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
];

const CULTURE_PROJECT_ITEM_TAG_TYPES = TAG_TYPES_BY_LOG_CATEGORY[ACTIVITY_LOG_CATEGORIES.CULTURE_PROJECT].items;

const getSubmitSurveyInvalidatedTags = (projectId, keyAreasIds = []) => {
  const tags = [
    { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
    { type: API_TAG_TYPES.PERSONAL_TRACKER, id: projectId },
    { type: API_TAG_TYPES.PROJECT_DASHBOARD, id: projectId },
    { type: API_TAG_TYPES.DASHBOARD_PORTFOLIO, id: 'LIST' },
    { type: API_TAG_TYPES.MASTER_DASHBOARD, id: 'LIST' },
    { type: API_TAG_TYPES.MASTER_DASHBOARD_CULTURAL_PROJECTS, id: 'LIST' },
    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_CULTURE_PROJECTS, id: 'LIST' },
    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
    { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO_GRAPH },
  ];

  keyAreasIds.forEach((id) => {
    tags.push(
      ...[
        { type: API_TAG_TYPES.PROJECT_DASHBOARD_KEY_AREA_GRAPH, id },
        { type: API_TAG_TYPES.PROJECT_DASHBOARD_KEY_AREA_HEATMAP, id },
      ],
    );
  });

  return tags;
};

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProjectLimits: build.query({
      query: ({ projectId }) => ({ url: `/projects/${projectId}/date-limits/` }),
      transformResponse: (baseQueryReturnValue) => ({
        date_from: baseQueryReturnValue.date_from,
        date_to: baseQueryReturnValue.date_to || baseQueryReturnValue.current_date,
      }),
    }),
    getProjectSettings: build.query({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/get-project-data/`,
        params: { preview_mode: true },
      }),
      providesTags: (result, _error, { projectId }) => [
        { type: API_TAG_TYPES.CULTURE_PROJECT, id: result?.id ?? projectId },
      ],
      transformResponse: (data) => formatProjectData(data),
    }),
    getProjectSurveyData: build.query({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/get-project-data/`,
        params: {
          preview_mode: false,
          private_link: true,
        },
      }),
      transformResponse: (data) => formatProjectData(data),
    }),
    createProject: build.mutation({
      query: (data) => ({
        url: `/projects/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        result
          ? [
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
              ...(result.status !== PROJECT_STATUSES.DRAFT ? PROJECT_MUTATION_NON_DRAFT_TAGS : []),
              ...(result.status === PROJECT_STATUSES.ACTIVE && arg?.status !== PROJECT_STATUSES.ACTIVE
                ? [{ type: API_TAG_TYPES.ORGANISATIONS, id: result.organisation }]
                : []),
            ]
          : [],
    }),
    updateProject: build.mutation({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        result?.id
          ? [
              { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
              ...(result.status !== PROJECT_STATUSES.DRAFT ? PROJECT_MUTATION_NON_DRAFT_TAGS : []),
              ...(result.status === PROJECT_STATUSES.DELETED
                ? [
                    { type: API_TAG_TYPES.ORGANISATION_ACTIVITY_LOGS, id: 'LIST' },
                    ...CULTURE_PROJECT_ITEM_TAG_TYPES.map((type) => ({ type, id: result.id })),
                  ]
                : [{ type: API_TAG_TYPES.CULTURE_PROJECT, id: result.id }]),
              ...(result.status === PROJECT_STATUSES.ACTIVE && arg?.status !== PROJECT_STATUSES.ACTIVE
                ? [{ type: API_TAG_TYPES.ORGANISATIONS, id: result.organisation }]
                : []),
            ]
          : [],
    }),
    duplicateProject: build.mutation({
      query: ({ id, selectedOrganisation }) => ({
        url: `/projects/${id}/duplicate/`,
        method: REQUEST_METHODS.POST,
        body: { organisation_id: selectedOrganisation },
      }),
      transformResponse: (baseQueryReturnValue) => baseQueryReturnValue.project,
      invalidatesTags: (result) => (result ? [{ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' }] : []),
    }),
    deleteProject: build.mutation({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, error) => (!error ? [{ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' }] : []),
    }),
    getProjectUsers: build.query({
      query: ({ projectId, params = {} }) => ({
        url: `/projects/${projectId}/users/`,
        params,
      }),
      transformResponse: (baseQueryReturnValue = []) =>
        baseQueryReturnValue.map((recipient) => ({
          ...recipient,
          id: recipient.user_id,
        })),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ user_id: id }) => ({ type: API_TAG_TYPES.PROJECT_USERS, id })),
              { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' }],
    }),
    createProjectLeadersList: build.mutation({
      query: ({ projectId }) => ({ url: `/projects/${projectId}/users/` }),
    }),
    getProjectUserById: build.query({
      query: ({ projectId, userId }) => ({ url: `/projects/${projectId}/users/${userId}/` }),
    }),
    createProjectUsers: build.mutation({
      query: (data) => ({
        url: '/projects/users/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.CULTURE_PROJECT_INVITES, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_INVITES, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
        { type: API_TAG_TYPES.MASTER_DASHBOARD_CULTURAL_PROJECTS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
      ],
    }),
    deactivateProjectUsers: build.mutation({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/users/deactivate/`,
        method: REQUEST_METHODS.DELETE,
        body: data,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
      ],
    }),
    updateProjectUsersRoles: build.mutation({
      query: ({ data, projectId }) => ({
        url: `/projects/${projectId}/roles/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
        { type: API_TAG_TYPES.MASTER_DASHBOARD_CULTURAL_PROJECTS, id: 'LIST' },
        { type: API_TAG_TYPES.DASHBOARD_PORTFOLIO, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
      ],
    }),
    updateProjectUsersRolesBulk: build.mutation({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/roles-bulk/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' }],
    }),
    downloadProjectCsv: build.mutation({
      queryFn: async ({ id, name }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/projects/${id}/download-csv/`,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, name);

        return { data: null };
      },
    }),
    getKeyEventsLimits: build.query({
      query: ({ projectId }) => ({ url: `/projects/${projectId}/dataset-dates/` }),
      transformResponse: (keyEventLimits) => {
        if (isEmpty(keyEventLimits)) {
          return [];
        }

        const today = new Date();
        const { datasets, last_key_event_create_date: lastKeyEventDate = null } = keyEventLimits;
        const datasetsLimits = datasets
          .map((dataset) => ({
            start: new Date(dataset.start_date),
            end: new Date(dataset.end_date),
          }))
          .toSorted((a, b) => a.end.getTime() - b.end.getTime());

        const [firstDataset, ...otherDatasets] = datasetsLimits;
        const lastDataset = otherDatasets.length ? otherDatasets[otherDatasets.length - 1] : firstDataset;
        const lastRangeStartDate =
          lastDataset.end.getTime() < today.getTime() ? today : getTomorrowDate(lastDataset.end);
        const lastRange = {
          start: formatDate(lastRangeStartDate, 'YYYY/MM/DD'),
          end: formatDate(lastKeyEventDate, 'YYYY/MM/DD', getLastKeyEventDatePlaceholder(lastRangeStartDate)),
        };

        return [
          ...otherDatasets.map((dataset) => ({
            start: formatDate(dataset.start, 'YYYY/MM/DD'),
            end: formatDate(dataset.end, 'YYYY/MM/DD'),
          })),
          lastRange,
        ];
      },
    }),
    getKeyEvents: build.query({
      query: ({ projectId }) => ({
        url: '/projects/key-events/',
        params: { project_id: projectId },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.KEY_EVENTS, id })),
              { type: API_TAG_TYPES.KEY_EVENTS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.KEY_EVENTS, id: 'LIST' }],
    }),
    addKeyEvent: build.mutation({
      query: (data) => ({
        url: '/projects/key-events/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.KEY_EVENTS, id: 'LIST' }],
    }),
    updateKeyEvent: build.mutation({
      query: ({ keyEventId, data }) => ({
        url: `/projects/key-events/${keyEventId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: (result) => (result ? [{ type: API_TAG_TYPES.KEY_EVENTS, id: result.id }] : []),
    }),
    deleteKeyEvent: build.mutation({
      query: ({ keyEventId }) => ({
        url: `/projects/key-events/${keyEventId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.KEY_EVENTS, id: 'LIST' }],
    }),
    getProjectUsersCsv: build.mutation({
      queryFn: async ({ projectId, params, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/projects/${projectId}/users/download-csv/`,
          params,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    sendProjectSurveyResponse: build.mutation({
      query: (data) => ({
        url: '/projects/survey/',
        method: REQUEST_METHODS.POST,
        body: { ...data, private_link: true },
      }),
      invalidatesTags: (result, _error, { project: id, keyAreasIds }) =>
        result ? getSubmitSurveyInvalidatedTags(id, keyAreasIds) : [],
    }),
    getPublicProjectSurveyData: build.query({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/get-project-data/`,
        params: { preview_mode: false },
      }),
      extraOptions: { isPublic: true },
      transformResponse: (data) => formatProjectData(data),
    }),
    sendPublicProjectSurveyResponse: build.mutation({
      query: (data) => ({
        url: '/projects/survey/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      extraOptions: { isPublic: true },
      invalidatesTags: (result, _error, { project: id, keyAreasIds }) =>
        result ? getSubmitSurveyInvalidatedTags(id, keyAreasIds) : [],
    }),
    editGroupSegments: build.mutation({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/edit-group-segments/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          if (data?.message) {
            addNotification(data.message, NOTIFICATION_TYPES.SUCCESS);
          }
        } catch {
          // the failure is surfaced by the calling hook
        }
      },
      invalidatesTags: (result, error, { projectId }) =>
        result && projectId
          ? [
              { type: API_TAG_TYPES.CULTURE_PROJECT, id: projectId },
              { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
            ]
          : [],
    }),
    getProjectsJournalsList: build.query({
      query: (params) => ({
        url: `/projects/journals/`,
        params,
      }),
      providesTags: (result) => (result ? [{ type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' }] : []),
    }),
    deleteProjectJournal: build.mutation({
      query: ({ projectId, journalId }) => ({
        url: `/projects/${projectId}/journals/${journalId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
        { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
        { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
      ],
    }),
    getProjectJournalEntries: build.query({
      query: ({ journalId, params }) => ({
        url: `/projects/journals/${journalId}/`,
        params,
      }),
      providesTags: (result) =>
        result?.id ? [{ type: API_TAG_TYPES.PROJECTS_JOURNAL_ENTRIES_LIST, id: result.id }] : [],
    }),
    getProjectJournalEntryById: build.query({
      query: ({ projectId, journalId, entryId }) => ({
        url: `/projects/${projectId}/journals/${journalId}/entries/${entryId}/`,
      }),
      keepUnusedDataFor: 0,
    }),
    updateProjectJournalEntry: build.mutation({
      query: ({ projectId, journalId, entryId, data }) => ({
        url: `/projects/${projectId}/journals/${journalId}/entries/${entryId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
    }),
    updateKeyAreaReflection: build.mutation({
      query: ({ reflectionId, data }) => ({
        url: `/projects/reflections/${reflectionId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
    }),
    addProjectJournalAction: build.mutation({
      query: (data) => ({
        url: '/projects/journal-actions/',
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
            ]
          : [],
    }),
    updateProjectJournalAction: build.mutation({
      query: ({ actionId, data }) => ({
        url: `/projects/journal-actions/${actionId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;
        } catch (error) {
          const errorData = error?.error?.data;
          const [fieldKey, messages] = Object.entries(errorData || {})[0] || [];
          const errorMessage =
            fieldKey && messages?.[0]
              ? `${ACTION_FIELD_LABELS[fieldKey] || fieldKey}: ${messages[0]}`
              : 'Failed to update action';

          addNotification(errorMessage);
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
            ]
          : [],
    }),
    deleteProjectJournalAction: build.mutation({
      query: ({ actionId }) => ({
        url: `/projects/journal-actions/${actionId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, _error, { journalId }) => [
        { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
        { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNAL_ENTRIES_LIST, id: journalId },
      ],
    }),
    deleteProjectJournalEntry: build.mutation({
      query: ({ projectId, journalId, entryId }) => ({
        url: `/projects/${projectId}/journals/${journalId}/entries/${entryId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, _error, { journalId }) => [
        { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
        { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNAL_ENTRIES_LIST, id: journalId },
      ],
    }),
    addThemePrioritisationItem: build.mutation({
      query: ({ entry, text }) => ({
        url: '/projects/theme-prioritisation-items/',
        method: REQUEST_METHODS.POST,
        body: { entry, text },
      }),
    }),
    updateThemePrioritisationItem: build.mutation({
      query: ({ itemId, text }) => ({
        url: `/projects/theme-prioritisation-items/${itemId}/`,
        method: REQUEST_METHODS.PATCH,
        body: { text },
      }),
    }),
    deleteThemePrioritisationItem: build.mutation({
      query: ({ itemId }) => ({
        url: `/projects/theme-prioritisation-items/${itemId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
    }),
    reorderThemePrioritisationItems: build.mutation({
      query: ({ entry, items }) => ({
        url: '/projects/theme-prioritisation-items/reorder/',
        method: REQUEST_METHODS.POST,
        body: { entry, items },
      }),
    }),
    getActionOwners: build.query({
      query: (params) => ({
        url: '/projects/action-owners/',
        params,
      }),
      providesTags: (result) => (result ? [{ type: API_TAG_TYPES.ACTION_OWNERS, id: 'LIST' }] : []),
    }),
    createProjectJournalEntry: build.mutation({
      query: ({ projectId, journalId, data }) => ({
        url: `/projects/${projectId}/journals/${journalId}/entries/`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (result) =>
        result?.id
          ? [
              { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
              { type: API_TAG_TYPES.PROJECTS_JOURNAL_ENTRIES_LIST, id: result.journal },
            ]
          : [],
    }),
    getActionTrackerList: build.query({
      query: (params) => ({
        url: `/projects/action-tracker/?${formatSearchParams(params)}`,
      }),
      providesTags: (result) => (result ? [{ type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' }] : []),
    }),
    getActionTrackerProjects: build.query({
      query: (params) => ({
        url: `/projects/action-tracker/projects/?${formatSearchParams(params)}`,
      }),
      transformResponse: (response) => ({
        ...response,
        results: response.results.map((item) => ({
          ...item,
          id: item.project_id,
        })),
      }),
      providesTags: [{ type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' }],
    }),
    getActionTrackerSegments: build.query({
      query: (params) => ({
        url: '/projects/action-tracker/segments/',
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' }],
    }),
    updateActionTrackerItem: build.mutation({
      query: ({ actionId, data }) => ({
        url: `/projects/action-tracker/${actionId}/`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' }],
    }),
    deleteActionTrackerItem: build.mutation({
      query: ({ actionId }) => ({
        url: `/projects/action-tracker/${actionId}/`,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, _error, { journalId }) => [
        { type: API_TAG_TYPES.ACTION_TRACKER, id: 'LIST' },
        { type: API_TAG_TYPES.ACTION_TRACKER_SEGMENTS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNAL_ENTRIES_LIST, id: journalId },
      ],
    }),
  }),
});

export const {
  useGetProjectLimitsQuery,
  useGetProjectSettingsQuery,
  useGetProjectSurveyDataQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDuplicateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectUsersQuery,
  useCreateProjectLeadersListMutation,
  useGetProjectUserByIdQuery,
  useCreateProjectUsersMutation,
  useDeactivateProjectUsersMutation,
  useUpdateProjectUsersRolesMutation,
  useUpdateProjectUsersRolesBulkMutation,
  useDownloadProjectCsvMutation,
  useGetKeyEventsLimitsQuery,
  useGetKeyEventsQuery,
  useAddKeyEventMutation,
  useUpdateKeyEventMutation,
  useDeleteKeyEventMutation,
  useGetProjectUsersCsvMutation,
  useSendProjectSurveyResponseMutation,
  useGetPublicProjectSurveyDataQuery,
  useSendPublicProjectSurveyResponseMutation,
  useEditGroupSegmentsMutation,
  useGetProjectsJournalsListQuery,
  useDeleteProjectJournalMutation,
  useGetProjectJournalEntriesQuery,
  useLazyGetProjectJournalEntryByIdQuery,
  useUpdateProjectJournalEntryMutation,
  useUpdateKeyAreaReflectionMutation,
  useAddProjectJournalActionMutation,
  useUpdateProjectJournalActionMutation,
  useLazyGetActionOwnersQuery,
  useCreateProjectJournalEntryMutation,
  useGetActionTrackerListQuery,
  useGetActionTrackerProjectsQuery,
  useGetActionTrackerSegmentsQuery,
  useUpdateActionTrackerItemMutation,
  useDeleteProjectJournalActionMutation,
  useDeleteProjectJournalEntryMutation,
  useDeleteActionTrackerItemMutation,
  useAddThemePrioritisationItemMutation,
  useUpdateThemePrioritisationItemMutation,
  useDeleteThemePrioritisationItemMutation,
  useReorderThemePrioritisationItemsMutation,
} = projectsApi;
